<template>
    <div>
        <template v-if="isMultiBranch">
            <p class="mb-2">{{ translate("page.category_manager.append_branch_multi_intro") }}</p>
            <div class="append-preview">
                <div v-for="branch in branches" :key="branch.name" class="append-preview__row append-preview__row--new">
                    <span class="icon"><i class="fas fa-plus"></i></span>
                    <span>{{ translate("page.category_manager.append_branch_multi_row", false, [branch.name, branch.count]) }}</span>
                </div>
            </div>
        </template>

        <template v-else>
            <div class="field" :class="{ 'has-error': showError && errorKey !== null }">
                <label class="label">{{ translate("page.category_manager.append_branch_name_label") }}</label>
                <div class="control">
                    <input
                        ref="nameInput"
                        :value="modelValue"
                        class="input"
                        :class="{ 'is-danger': showError && errorKey !== null }"
                        type="text"
                        :maxlength="BRANCH_NAME_MAX_LENGTH"
                        :placeholder="translate('page.category_manager.append_branch_name_label')"
                        @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
                    />
                </div>
                <p v-if="showError && errorKey !== null" class="help is-danger">
                    {{ translate(errorKey) }}
                </p>
            </div>

            <div class="append-preview mt-4">
                <div class="append-preview__row">
                    <span class="icon"><i class="fas fa-folder"></i></span>
                    <span>{{
                        translate("page.category_manager.append_branch_preview_existing", false, [store.totalCategoryCount, rootName])
                    }}</span>
                </div>
                <div class="append-preview__row append-preview__row--new">
                    <span class="icon"><i class="fas fa-plus"></i></span>
                    <span>{{
                        translate("page.category_manager.append_branch_preview_new", false, [
                            modelValue.trim() || translate("page.category_manager.append_branch_name_label"),
                            newBranchCount,
                        ])
                    }}</span>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import { translate } from "../Common/translations";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import { BRANCH_NAME_MAX_LENGTH, branchNameErrorKey } from "./branchName";

    const props = defineProps<{
        modelValue: string;
        newBranchCount: number;
        showError: boolean;
        branches?: { name: string; count: number }[];
    }>();

    const emit = defineEmits<{
        "update:modelValue": [value: string];
    }>();

    const store = useCategoriesManagerStore();
    const nameInput = ref<HTMLInputElement | null>(null);

    const isMultiBranch = computed(() => (props.branches?.length ?? 0) > 0);
    // No name to validate when the branches are derived from templates.
    const errorKey = computed(() => (isMultiBranch.value ? null : branchNameErrorKey(props.modelValue)));
    const rootName = computed(() => store.rootNode?.name ?? "");

    function focus() {
        nameInput.value?.focus();
    }

    defineExpose({ errorKey, focus, isMultiBranch });
</script>

<style scoped>
    .append-preview__row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.6rem 0.75rem;
        border: 1px solid var(--bulma-border, #384754);
        border-radius: var(--bulma-radius, 0.375rem);
    }

    .append-preview__row + .append-preview__row {
        margin-top: 0.5rem;
    }

    .append-preview__row--new {
        border-style: dashed;
        border-color: var(--bulma-success, #48c78e);
    }
</style>
